import { useState, useEffect, useRef } from 'react';
import { useContract } from './useContract';

// Admin addresses - Update these with your actual admin/owner addresses
const ADMIN_ADDRESSES = [
  '0xCf3DC537a72cf65D094299D10A61018406c43E34'.toLowerCase(),
];

const OWNER_ADDRESSES = [
  '0xFE388e83D35eda4B3f93cD4393F92a02f808082F'.toLowerCase(),
];

export const useAuth = (account, network) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [contractAdmin, setContractAdmin] = useState(null);
  const [contractOwner, setContractOwner] = useState(null);

  // Prevent infinite loops
  const hasCheckedRef = useRef(false);
  const lastAccountRef = useRef(null);

  // Get contract from hook (but don't depend on the full hook to prevent loops)
  const { contract } = useContract(null, null, network);

  useEffect(() => {
    const checkAuth = async () => {
      // Type check and validation - handle all possible cases
      if (!account || 
          typeof account !== 'string' || 
          account.length === 0 ||
          account === '' ||
          account === '0x0' ||
          account === '0x0000000000000000000000000000000000000000') {
        console.log('Auth check skipped - invalid account:', account, 'type:', typeof account);
        
        // Reset auth state for invalid accounts
        setIsAdmin(false);
        setIsOwner(false);
        setContractAdmin(null);
        setContractOwner(null);
        hasCheckedRef.current = false;
        lastAccountRef.current = null;
        return;
      }

      // Prevent infinite loops and duplicate checks
      if (hasCheckedRef.current && lastAccountRef.current === account) {
        return;
      }

      // Reset check state when account changes
      if (lastAccountRef.current !== account) {
        hasCheckedRef.current = false;
        lastAccountRef.current = account;
      }

      hasCheckedRef.current = true;
      
      // Safe string conversion
      let accountLower;
      try {
        accountLower = account.toLowerCase();
      } catch (error) {
        console.error('Error converting account to lowercase:', error);
        return;
      }
      
      // IMMEDIATE fallback check - no dependencies, no loops
      const fallbackAdmin = ADMIN_ADDRESSES.includes(accountLower);
      const fallbackOwner = OWNER_ADDRESSES.includes(accountLower);
      
      // Set immediately for fast UI response
      setIsAdmin(fallbackAdmin);
      setIsOwner(fallbackOwner);
      
      console.log('Auth check for account:', accountLower);
      console.log('Fallback auth:', { fallbackAdmin, fallbackOwner });
      
      // Optional contract check if available
      if (contract) {
        try {
          const [adminFromContract, ownerFromContract] = await Promise.all([
            contract.admin(),
            contract.owner()
          ]);

          if (adminFromContract && ownerFromContract) {
            const adminLower = adminFromContract.toLowerCase();
            const ownerLower = ownerFromContract.toLowerCase();

            setContractAdmin(adminLower);
            setContractOwner(ownerLower);

            // Update with contract data if available
            const isAdminAccount = (adminLower === accountLower) || fallbackAdmin;
            const isOwnerAccount = (ownerLower === accountLower) || fallbackOwner;

            setIsAdmin(isAdminAccount);
            setIsOwner(isOwnerAccount);

            console.log('Contract auth completed:', {
              account: accountLower,
              contractAdmin: adminLower,
              contractOwner: ownerLower,
              isAdmin: isAdminAccount,
              isOwner: isOwnerAccount
            });
          }
        } catch (error) {
          console.error('Contract auth failed, using fallback:', error);
          // Keep fallback values - already set above
        }
      } else {
        console.log('Using fallback auth only (no contract)');
        // Keep fallback values - already set above
      }
    };

    // Small delay to prevent immediate execution loops
    const timer = setTimeout(checkAuth, 100);
    return () => clearTimeout(timer);
  }, [account, contract, network]);

  // Reset check state when account changes
  useEffect(() => {
    if (lastAccountRef.current !== account) {
      hasCheckedRef.current = false;
      setIsAdmin(false);
      setIsOwner(false);
      setContractAdmin(null);
      setContractOwner(null);
    }
  }, [account]);

  return {
    isAdmin,
    isOwner,
    contractAdmin,
    contractOwner,
    isAdminOrOwner: isAdmin || isOwner
  };
};